import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Badge, Button, HelpTip } from '@/components/ui';
import { api } from '@/lib/api';

import { formatTime } from '@/lib/format-date';
/**
 * 🔴 A LISTA DE TRABALHO — os grupos sem o bot, e os clientes descobertos.
 *
 * > **Matheus:** *"Uma lista de trabalho, para eu ir marcando."*
 *
 * ── Por que é o item de maior impacto da rodada
 *
 * **49 dos 53 grupos estão sem o bot** — 528 das 689 combinações de grupo ×
 * aviso não recebem nada por causa disso. ⚠️ **Nenhum interruptor, perfil ou
 * modal muda isso enquanto o bot estiver fora.**
 *
 * ── 🔴 Ele não marca nada
 *
 * O trabalho é dele, no celular: entrar no grupo e adicionar o número do bot.
 * **Quem confirma é o sistema** — pela MESMA conferência que o botão "Buscar
 * grupos" já fazia (`POST /grupos/sincronizar`, leitura pura na Evolution, que
 * devolve quantos grupos o bot **voltou** a ter). ❌ Nenhum motor novo, nenhuma
 * caixinha para marcar à mão.
 *
 * ⚠️ **Por que conferir com a tela aberta, e não por um relógio:** ele adiciona
 * os grupos em sequência, com a tela do lado. Enquanto ela está aberta a
 * conferência roda sozinha a cada 30 s e o progresso anda; fechada, não gasta
 * chamada nenhuma. Um cron perguntaria à Evolution o dia inteiro para achar uma
 * mudança que só acontece quando ele está trabalhando.
 */

interface GrupoSemBot {
  grupoId: string;
  nome: string;
  interno: boolean;
  deQuem: string | null;
}

interface Descoberto {
  clienteId: string;
  nome: string;
  equipamentos: number;
  ultimoContatoIso: string | null;
}

interface Trabalho {
  bot: { comBot: number; total: number; faltam: number; grupos: GrupoSemBot[] };
  descobertos: Descoberto[];
}

const SEGUNDOS_ENTRE_CONFERENCIAS = 30;

export function ListaDeTrabalho({ onMudou }: { onMudou?: () => void }) {
  const [dados, setDados] = useState<Trabalho | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [ultimaConferencia, setUltimaConferencia] = useState<string | null>(null);
  const [voltaram, setVoltaram] = useState(0);
  const montado = useRef(true);

  const carregar = useCallback(() => {
    api
      .get<Trabalho>('/api/v1/admin/whatsapp/trabalho')
      .then((t) => montado.current && setDados(t))
      .catch((e: Error) => montado.current && setErro(e.message));
  }, []);

  /** 🔴 A conferência — a mesma do botão "Buscar grupos". Leitura pura. */
  const conferir = useCallback(async () => {
    setConferindo(true);
    setErro(null);
    try {
      const r = await api.post<{ voltaram?: number }>('/api/v1/admin/whatsapp/grupos/sincronizar', {});
      if (!montado.current) return;
      setVoltaram(r.voltaram ?? 0);
      setUltimaConferencia(formatTime(new Date()));
      carregar();
      onMudou?.();
    } catch (err) {
      /**
       * ⚠️ A conferência fala com o WhatsApp, e ele pode não responder. O erro
       * cru ("Erro 502") não diz nada a quem está adicionando grupos no celular
       * — e, pior, não diz que **a lista abaixo continua valendo**: ela vem do
       * nosso banco, não da Evolution.
       */
      if (montado.current) {
        setErro(
          'Não foi possível falar com o WhatsApp agora para conferir. A lista abaixo continua ' +
            'valendo — ela é a última conferência que deu certo. Tente de novo em instantes.',
        );
        setUltimaConferencia(null);
      }
    } finally {
      if (montado.current) setConferindo(false);
    }
  }, [carregar, onMudou]);

  useEffect(() => {
    montado.current = true;
    carregar();
    void conferir();
    const t = setInterval(() => void conferir(), SEGUNDOS_ENTRE_CONFERENCIAS * 1000);
    return () => {
      montado.current = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (erro && !dados) return <Alert variant="danger">{erro}</Alert>;
  if (!dados) return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;

  const { bot, descobertos } = dados;
  const pct = bot.total > 0 ? Math.round((bot.comBot / bot.total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="wa-lista-de-trabalho">
      {erro && <Alert variant="warning">{erro}</Alert>}

      {/* ─── Os grupos sem o bot ─── */}
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <h4 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Grupos que ainda não têm o bot
          </h4>
          <HelpTip texto="Enquanto o número do bot não estiver dentro do grupo, nenhum aviso daquele condomínio chega — nem os que estão ligados. Você adiciona o bot pelo WhatsApp, no celular; esta tela confere sozinha a cada meio minuto e vai marcando o progresso. Não há nada para marcar à mão aqui." />
        </div>

        <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid="wa-progresso-bot">
          <strong>
            {bot.comBot} de {bot.total}
          </strong>{' '}
          {bot.faltam === 0 ? '— o bot está em todos.' : `— faltam ${bot.faltam}.`}
        </p>

        {/* A barra: o progresso que ele vê andar enquanto trabalha. */}
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-paper-warm dark:bg-paper-dark-warm">
          <div
            className="h-full rounded-full bg-green transition-[width] duration-500"
            style={{ width: `${pct}%` }}
            data-testid="wa-progresso-barra"
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => void conferir()} disabled={conferindo} data-testid="wa-conferir-agora">
            {conferindo ? 'Conferindo…' : 'Conferir agora'}
          </Button>
          <span className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid="wa-conferencia-quando">
            {ultimaConferencia
              ? `Conferido às ${ultimaConferencia} · confere sozinho a cada ${SEGUNDOS_ENTRE_CONFERENCIAS}s com esta tela aberta`
              : conferindo
                ? 'Conferindo…'
                : 'Ainda não deu para conferir com o WhatsApp.'}
          </span>
          {voltaram > 0 && (
            <Badge variant="success" size="sm" data-testid="wa-voltaram">
              ✅ o bot voltou a {voltaram} {voltaram === 1 ? 'grupo' : 'grupos'}
            </Badge>
          )}
        </div>

        {bot.faltam > 0 && (
          <ul className="mt-3 space-y-1" data-testid="wa-grupos-sem-bot">
            {bot.grupos.map((g) => (
              <li
                key={g.grupoId}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-line px-2.5 py-2 dark:border-line-dark"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink-1 dark:text-ink-dark-1">{g.nome}</span>
                <span className="flex items-center gap-2 text-xs text-ink-3 dark:text-ink-dark-3">
                  {g.interno && (
                    <Badge variant="neutral" size="sm">
                      da IGM
                    </Badge>
                  )}
                  {g.deQuem ?? (g.interno ? '' : 'sem cliente ligado')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ─── Os clientes descobertos (#288) ─── */}
      <div className="border-t border-line pt-4 dark:border-line-dark">
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <h4 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Clientes monitorados e sem ninguém para avisar
          </h4>
          <HelpTip texto="Estes clientes têm coletor instalado e equipamentos sendo monitorados, mas nenhum grupo de WhatsApp ligado a eles. Se um equipamento cair, o aviso não vai para lugar nenhum — fica só registrado na Trilha. O número de equipamentos diz o tamanho do que está descoberto. Para resolver, ligue um grupo ao cliente em Vínculos." />
        </div>

        {descobertos.length === 0 ? (
          <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid="wa-descobertos-vazio">
            Todo cliente com coletor tem pelo menos um grupo conferido. Nada descoberto.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="wa-descobertos">
            {descobertos.map((c) => (
              <li
                key={c.clienteId}
                className={[
                  'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border px-2.5 py-2',
                  c.equipamentos > 0
                    ? 'border-red/40 bg-red-soft dark:border-red/40 dark:bg-red-soft-dark'
                    : 'border-line dark:border-line-dark',
                ].join(' ')}
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink-1 dark:text-ink-dark-1">{c.nome}</span>
                <span className="text-xs text-ink-2 dark:text-ink-dark-2">
                  {c.equipamentos === 0
                    ? 'nenhum equipamento monitorado'
                    : `${c.equipamentos} ${c.equipamentos === 1 ? 'equipamento monitorado' : 'equipamentos monitorados'}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
